import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { UserStore } from './user.store';
import { GroupJourneyService } from '../service/group-journey.service';
import { JourneyChatMessage } from '../utils/commonTypes';
import { GroupJourneyStore } from './group-journey.store';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';

type ChatState = {
  selectedChatId: string | null;
  selectedChatTitle:string;

  openedJourneyId: string | null;
  chatCache: { chatId: string, journeyId: string, title: string, messages: JourneyChatMessage[] }[];
  isLoading: boolean;
  isSending: boolean;
};

const initialState: ChatState = {
  selectedChatId: null,
  selectedChatTitle:'',
  openedJourneyId: null,
  chatCache: [],
  isLoading: false,
  isSending: false,
};

export const ChatStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withStorageSync({
    key: 'chat-store-synced',
    autoSync: true,
    select: ((state: ChatState) => ({
      selectedChatId: state.selectedChatId,
      selectedChatTitle: state.selectedChatTitle,
      openedJourneyId: state.openedJourneyId
    })) as (state: any) => Partial<any>,
  }),

  /** ✅ Selector: Retrieves the selected content from the cache */
  withComputed(({ selectedChatId, chatCache, openedJourneyId }) => ({
    messages: computed(() =>
      // chatCache().find(chat => chat.chatId === selectedChatId() && chat.journeyId === openedJourneyId())?.messages.sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()) ?? null
      chatCache().find(chat => chat.chatId === selectedChatId() && chat.journeyId === openedJourneyId())?.messages ?? null
    ),
    selectedChat: computed(() =>
      chatCache().find(chat => chat.chatId === selectedChatId() && chat.journeyId === openedJourneyId()) ?? null
    ),

  })),

  withMethods((store,gjStore = inject(GroupJourneyStore), userStore = inject(UserStore), gjService = inject(GroupJourneyService)) => ({

    reset(){
      patchState(store,{
        selectedChatId:null,
        selectedChatTitle:''
      });
    },

    //MUST BE CALLED PRIOR TO LAODING CHATS
    selectGroupJourneyWideChat(title:string){
      patchState(store,{
        selectedChatId:'GROUP',
        selectedChatTitle:title,
      });
    },

    //MUST BE CALLED PRIOR TO LAODING CHATS
    selectDiscussionChat(chatId:string,title:string){
      patchState(store,{
        selectedChatId:chatId,
        selectedChatTitle:title,
      });
    },

    async loadChat(journeyId:string,forceReload:boolean=true): Promise<boolean> {
      
      const chatId = store.selectedChatId();
      if(!chatId){
        return false;
      }

      // const journeyId = gjStore.selectedJourneyId();
      // if (!journeyId) {
      //   return false;
      // }

      //Check if we have the chat in the cache
      const cachedChat = store.chatCache().find(c => c.chatId == chatId && c.journeyId == journeyId);
      if (cachedChat && !forceReload) {
        patchState(store, { 
          selectedChatId:chatId,
          openedJourneyId:journeyId,
        });
        return true;
      }

      patchState(store, { isLoading: true });
      const result = await gjService.loadChatMessages(journeyId, chatId);


      const members = gjStore.groupJourneys().find(j=>j.id==journeyId)?.members;        
      const user = userStore.profile();


      const msgs = result.messages.map(m=>({
        ...m,
        isMine:m.senderId==user?.id?true:false,
        senderName:members?.find(member=>member.userId==m.senderId)?.name,
      }));

        patchState(store, {
          isLoading: false,
          selectedChatId:chatId,
          openedJourneyId:journeyId,
          chatCache: [
            ...store.chatCache().filter(c => c.chatId == chatId && c.journeyId == journeyId),
            { chatId: chatId, journeyId: journeyId, title: store.selectedChatTitle(), messages: msgs }
          ]
        });
        return true;
    },
    
    async sendMessage(content: string){
      const selectedChat = store.selectedChat();
      if (!selectedChat) {
        return false;
      }
      //Try to send the message
      patchState(store, { isSending: true });

      const tempMessage = {
        id: 'temp-' + Math.random().toString(36).substring(2, 15),
        senderId: userStore.profile()?.id,
        dateTime: new Date().toISOString(),
        content: content,
        isMine: true,
        error: false,
      } as JourneyChatMessage;

      //Append temp message to the chat
      patchState(store, {
        chatCache: store.chatCache().map(chat => (
          chat.chatId == selectedChat.chatId && chat.journeyId == selectedChat.journeyId ?
            {
              ...chat, messages: [...chat.messages, {...tempMessage,isMine:true}]
            } : chat
        ))
      });
      patchState(store, { isSending: false });

      //Tries to send the message
      let sent=false;
      try{
        await gjService.sendChatMessage(selectedChat.journeyId, selectedChat.chatId, content);
        sent=true;
      }catch(e){
        console.error('Error sending message', e);
      }
        //Handle error
      if(!sent){
        //mark the message as error
        patchState(store, {
          chatCache: store.chatCache().map(chat => (
            chat.chatId == selectedChat.chatId && chat.journeyId == selectedChat.journeyId ?
              {
                ...chat, messages: chat.messages.map(m => (
                  m.id == tempMessage.id ?
                    { ...m, error: true } : m
                ))
              } : chat
          ))
        });
      }

    },

    pushNewMessage(id:string,chatId: string, journeyId: string, userId: string, date: string, content: string) {
      if(store.chatCache().find(c=>c.chatId==chatId && c.journeyId==journeyId)){
        const members = gjStore.groupJourneys().find(j=>j.id==journeyId)?.members;        

        const msg = {
          id: id,
          senderId: userId,
          dateTime: date,
          content: content,
        } as JourneyChatMessage;

        patchState(store, {
          chatCache: store.chatCache().map(chat => (
            chat.chatId == chatId && chat.journeyId == journeyId ?
              {
                ...chat, messages: [...chat.messages.filter(cm=>cm.id!=id), {...msg,
                  isMine:msg.senderId==userId?true:false,
                  senderName:members?.find(member=>member.userId==msg.senderId)?.name
                }]
              } : chat
          ))
        });
      }
    },
  }))
);



